// module_roots/Login.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../module_roots/AuthContext";

import {
  Eye,
  EyeOff,
  Lock,
  User,
  AlertCircle,
  X,
  CheckCircle,
  Activity,
  ArrowRight,
  Sun,
  Moon,
  Palette,
  Store,
  Pill,
  Building2,
  ShoppingCart,
  Coffee,
  Wrench,
  Car,
  Scissors,
  PaintBucket,
  Heart,
  Sparkles,
  Droplet,
  Syringe,
  Package,
  Scissors as ScissorsIcon,
  Shirt,
  Bike,
  Home,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './Login.css';
import logo from '../assets/images/logo.png';

// ============================================
// ROLE TO DASHBOARD ROUTES MAPPING - SIMPLIFIED
// ============================================
const ROUTES = {
  // Super Admin - kept separate
  'super_admin': '/DashboardSuperAdmin',
  'superadmin': '/DashboardSuperAdmin',
  
  // ALL BUSINESS ROLES - Unified Dashboard
  // The DashboardBusinessManager handles role-based menus internally
  
  // POS
  'admin_pos': '/Dashboard-Business-Manager',
  'manager_pos': '/Dashboard-Business-Manager',
  'user_pos': '/Dashboard-Business-Manager',
  'viewer_pos': '/Dashboard-Business-Manager',
  'salesman_pos': '/Dashboard-Business-Manager',
  
  // PO
  'admin_po': '/Dashboard-Business-Manager',
  'manager_po': '/Dashboard-Business-Manager',
  'user_po': '/Dashboard-Business-Manager',
  'viewer_po': '/Dashboard-Business-Manager',
  
  // Fabrics
  'admin_fabrics': '/Dashboard-Business-Manager',
  'manager_fabrics': '/Dashboard-Business-Manager',
  'user_fabrics': '/Dashboard-Business-Manager',
  'viewer_fabrics': '/Dashboard-Business-Manager',
  
  // Mechanics Thermal
  'admin_mechanicsth': '/Dashboard-Business-Manager',
  'manager_mechanicsth': '/Dashboard-Business-Manager',
  'user_mechanicsth': '/Dashboard-Business-Manager',
  'viewer_mechanicsth': '/Dashboard-Business-Manager',
  
  // Mechanics Large
  'admin_mechanicsap': '/Dashboard-Business-Manager',
  'manager_mechanicsap': '/Dashboard-Business-Manager',
  'user_mechanicsap': '/Dashboard-Business-Manager',
  'viewer_mechanicsap': '/Dashboard-Business-Manager',
  
  // Hardware Thermal
  'admin_hardwareth': '/Dashboard-Business-Manager',
  'manager_hardwareth': '/Dashboard-Business-Manager',
  'user_hardwareth': '/Dashboard-Business-Manager',
  'viewer_hardwareth': '/Dashboard-Business-Manager',
  
  // Hardware Large
  'admin_hardwareap': '/Dashboard-Business-Manager',
  'manager_hardwareap': '/Dashboard-Business-Manager',
  'user_hardwareap': '/Dashboard-Business-Manager',
  'viewer_hardwareap': '/Dashboard-Business-Manager',
  
  // Autos Thermal
  'admin_autosth': '/Dashboard-Business-Manager',
  'manager_autosth': '/Dashboard-Business-Manager',
  'user_autosth': '/Dashboard-Business-Manager',
  'viewer_autosth': '/Dashboard-Business-Manager',
  
  // Autos Large
  'admin_autosap': '/Dashboard-Business-Manager',
  'manager_autosap': '/Dashboard-Business-Manager',
  'user_autosap': '/Dashboard-Business-Manager',
  'viewer_autosap': '/Dashboard-Business-Manager',
  
  // Pharmacy
  'admin_pharmacy': '/Dashboard-Business-Manager',
  'manager_pharmacy': '/Dashboard-Business-Manager',
  'user_pharmacy': '/Dashboard-Business-Manager',
  'viewer_pharmacy': '/Dashboard-Business-Manager',
  
  // Pharmacy TAX
  'admin_pharmacytax': '/Dashboard-Business-Manager',
  'manager_pharmacytax': '/Dashboard-Business-Manager',
  'user_pharmacytax': '/Dashboard-Business-Manager',
  'viewer_pharmacytax': '/Dashboard-Business-Manager',
  
  // Cosmetics
  'admin_cosmetics': '/Dashboard-Business-Manager',
  'manager_cosmetics': '/Dashboard-Business-Manager',
  'user_cosmetics': '/Dashboard-Business-Manager',
  'viewer_cosmetics': '/Dashboard-Business-Manager',
  
  // Solar
  'admin_solar': '/Dashboard-Business-Manager',
  'manager_solar': '/Dashboard-Business-Manager',
  'user_solar': '/Dashboard-Business-Manager',
  'viewer_solar': '/Dashboard-Business-Manager',
  
  // Electronics
  'admin_electronics': '/Dashboard-Business-Manager',
  'manager_electronics': '/Dashboard-Business-Manager',
  'user_electronics': '/Dashboard-Business-Manager',
  'viewer_electronics': '/Dashboard-Business-Manager',
  
  // Appliances
  'admin_appliances': '/Dashboard-Business-Manager',
  'manager_appliances': '/Dashboard-Business-Manager',
  'user_appliances': '/Dashboard-Business-Manager',
  'viewer_appliances': '/Dashboard-Business-Manager',
  
  // PVC
  'admin_pvc': '/Dashboard-Business-Manager',
  'manager_pvc': '/Dashboard-Business-Manager',
  'user_pvc': '/Dashboard-Business-Manager',
  'viewer_pvc': '/Dashboard-Business-Manager',
  
  // Salanty
  'admin_salanty': '/Dashboard-Business-Manager',
  'manager_salanty': '/Dashboard-Business-Manager',
  'user_salanty': '/Dashboard-Business-Manager',
  'viewer_salanty': '/Dashboard-Business-Manager',
  
  // Default fallback
  'default': '/Dashboard-Business-Manager'
};

// ============================================
// TRANSLATIONS DICTIONARY
// ============================================
const TRANSLATIONS = {
  en: {
    welcome: "Welcome Back",
    slogan: "Unified Business Ecosystem",
    username: "Username",
    password: "Password",
    remember: "Remember me",
    forgot: "Forgot password?",
    signin: "Sign In",
    loading: "Authenticating...",
    contact: "Contact Administrator",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "Theme Color",
    access: "Access your unified workspace",
    clickHere: "Click here",
    user_inactive: "Account Inactive",
    user_inactive_message: "Your account is inactive. Please contact your administrator to reactivate your account.",
    user_suspended: "Account Suspended",
    user_suspended_message: "Your account has been suspended. Please contact your administrator.",
    subscription_expired: "⚠️ Subscription Expired",
    subscription_expired_message: "Your subscription has expired. Please renew to continue accessing the system.",
    subscription_cancelled: "Subscription Cancelled",
    subscription_cancelled_message: "Your subscription has been cancelled. Please contact your administrator to reactivate.",
    subscription_pending: "Subscription Pending",
    subscription_pending_message: "Your subscription is pending approval. Please wait for administrator approval.",
    no_subscription: "No Subscription Found",
    no_subscription_message: "No subscription found. Please purchase a subscription to access the system.",
    subscriptionStatus: "Subscription Status",
    totalSubscriptions: "Total",
    activeSubscriptions: "Active",
    expiredSubscriptions: "Expired",
    cancelledSubscriptions: "Cancelled",
    pendingSubscriptions: "Pending",
    daysRemaining: "Days Remaining",
    contactAdmin: "Please contact your administrator for assistance",
    close: "Close",
    renewNow: "Renew Now",
    userStatus: "Account Status",
    needHelp: "Need Help?",
    contactSupport: "Contact Support"
  },
  ur: {
    welcome: "خوش آمدید",
    slogan: "متحد بزنس ایکو سسٹم",
    username: "صارف نام",
    password: "پاس ورڈ",
    remember: "مجھے یاد رکھیں",
    forgot: "پاس ورڈ بھول گئے؟",
    signin: "لاگ ان کریں",
    loading: "تصدیق ہو رہی ہے...",
    contact: "ایڈمنسٹریٹر سے رابطہ کریں",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "تھیم کا رنگ",
    access: "اپنے متحد کام کی جگہ تک رسائی حاصل کریں",
    clickHere: "یہاں کلک کریں",
    user_inactive: "اکاؤنٹ غیر فعال",
    user_inactive_message: "آپ کا اکاؤنٹ غیر فعال ہے۔ براہ کرم دوبارہ فعال کرنے کے لیے اپنے منتظم سے رابطہ کریں۔",
    user_suspended: "اکاؤنٹ معطل",
    user_suspended_message: "آپ کا اکاؤنٹ معطل کر دیا گیا ہے۔ براہ کرم اپنے منتظم سے رابطہ کریں۔",
    subscription_expired: "⚠️ سبسکرپشن میعاد ختم",
    subscription_expired_message: "آپ کی سبسکرپشن میعاد ختم ہو گئی ہے۔ براہ کرم سسٹم تک رسائی جاری رکھنے کے لیے تجدید کریں۔",
    subscription_cancelled: "سبسکرپشن منسوخ",
    subscription_cancelled_message: "آپ کی سبسکرپشن منسوخ کر دی گئی ہے۔ براہ کرم دوبارہ فعال کرنے کے لیے اپنے منتظم سے رابطہ کریں۔",
    subscription_pending: "سبسکرپشن زیر التواء",
    subscription_pending_message: "آپ کی سبسکرپشن منظوری کے زیر التواء ہے۔ براہ کرم منتظم کی منظوری کا انتظار کریں۔",
    no_subscription: "کوئی سبسکرپشن نہیں",
    no_subscription_message: "کوئی سبسکرپشن نہیں ملی۔ براہ کرم سسٹم تک رسائی کے لیے سبسکرپشن خریدیں۔",
    subscriptionStatus: "سبسکرپشن کی حالت",
    totalSubscriptions: "کل",
    activeSubscriptions: "فعال",
    expiredSubscriptions: "میعاد ختم",
    cancelledSubscriptions: "منسوخ",
    pendingSubscriptions: "زیر التواء",
    daysRemaining: "باقی دن",
    contactAdmin: "براہ کرم مدد کے لیے اپنے منتظم سے رابطہ کریں",
    close: "بند کریں",
    renewNow: "ابھی تجدید کریں",
    userStatus: "اکاؤنٹ کی حالت",
    needHelp: "مدد چاہیے؟",
    contactSupport: "سپورٹ سے رابطہ کریں"
  },
  ar: {
    welcome: "مرحبا بعودتك",
    slogan: "النظام الإيكولوجي للأعمال الموحد",
    username: "اسم المستخدم",
    password: "كلمة المرور",
    remember: "تذكرني",
    forgot: "نسيت كلمة المرور؟",
    signin: "تسجيل الدخول",
    loading: "جاري المصادقة...",
    contact: "الاتصال بالمسؤول",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "لون السمة",
    access: "الوصول إلى مساحة العمل الموحدة الخاصة بك",
    clickHere: "انقر هنا",
    user_inactive: "الحساب غير نشط",
    user_inactive_message: "حسابك غير نشط. يرجى الاتصال بالمسؤول لإعادة التنشيط.",
    user_suspended: "الحساب معلق",
    user_suspended_message: "تم تعليق حسابك. يرجى الاتصال بالمسؤول.",
    subscription_expired: "⚠️ انتهت صلاحية الاشتراك",
    subscription_expired_message: "انتهت صلاحية اشتراكك. يرجى التجديد لمواصلة الوصول إلى النظام.",
    subscription_cancelled: "تم إلغاء الاشتراك",
    subscription_cancelled_message: "تم إلغاء اشتراكك. يرجى الاتصال بالمسؤول لإعادة التنشيط.",
    subscription_pending: "الاشتراك معلق",
    subscription_pending_message: "اشتراكك في انتظار الموافقة. يرجى انتظار موافقة المسؤول.",
    no_subscription: "لا يوجد اشتراك",
    no_subscription_message: "لا يوجد اشتراك. يرجى شراء اشتراك للوصول إلى النظام.",
    subscriptionStatus: "حالة الاشتراك",
    totalSubscriptions: "الإجمالي",
    activeSubscriptions: "نشط",
    expiredSubscriptions: "منتهي الصلاحية",
    cancelledSubscriptions: "ملغى",
    pendingSubscriptions: "معلق",
    daysRemaining: "الأيام المتبقية",
    contactAdmin: "يرجى الاتصال بالمسؤول الخاص بك للحصول على المساعدة",
    close: "إغلاق",
    renewNow: "جدد الآن",
    userStatus: "حالة الحساب",
    needHelp: "بحاجة إلى مساعدة؟",
    contactSupport: "اتصل بالدعم"
  },
  es: {
    welcome: "Bienvenido de nuevo",
    slogan: "Ecosistema Empresarial Unificado",
    username: "Nombre de usuario",
    password: "Contraseña",
    remember: "Recuérdame",
    forgot: "¿Olvidó su contraseña?",
    signin: "Iniciar sesión",
    loading: "Autenticando...",
    contact: "Contactar administrador",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "Color del tema",
    access: "Accede a tu espacio de trabajo unificado",
    clickHere: "Haga clic aquí",
    user_inactive: "Cuenta Inactiva",
    user_inactive_message: "Su cuenta está inactiva. Contacte al administrador para reactivar.",
    user_suspended: "Cuenta Suspendida",
    user_suspended_message: "Su cuenta ha sido suspendida. Contacte al administrador.",
    subscription_expired: "⚠️ Suscripción Expirada",
    subscription_expired_message: "Su suscripción ha expirado. Renueve para continuar accediendo al sistema.",
    subscription_cancelled: "Suscripción Cancelada",
    subscription_cancelled_message: "Su suscripción ha sido cancelada. Contacte al administrador para reactivar.",
    subscription_pending: "Suscripción Pendiente",
    subscription_pending_message: "Su suscripción está pendiente de aprobación. Espere la aprobación del administrador.",
    no_subscription: "Sin Suscripción",
    no_subscription_message: "No se encontró una suscripción. Adquiera una suscripción para acceder al sistema.",
    subscriptionStatus: "Estado de Suscripción",
    totalSubscriptions: "Total",
    activeSubscriptions: "Activas",
    expiredSubscriptions: "Expiradas",
    cancelledSubscriptions: "Canceladas",
    pendingSubscriptions: "Pendientes",
    daysRemaining: "Días Restantes",
    contactAdmin: "Contacte a su administrador para asistencia",
    close: "Cerrar",
    renewNow: "Renovar Ahora",
    userStatus: "Estado de la Cuenta",
    needHelp: "¿Necesita ayuda?",
    contactSupport: "Contactar Soporte"
  }
};

const LANGUAGES = {
  en: { name: 'English', icon: '🇺🇸', dir: 'ltr' },
  ur: { name: 'اردو', icon: '🇵🇰', dir: 'rtl' },
  ar: { name: 'عربي', icon: '🇸🇦', dir: 'rtl' },
  es: { name: 'Español', icon: '🇪🇸', dir: 'ltr' }
};

// ============================================
// THEME COLORS (Enhanced)
// ============================================
const THEME_COLORS = [
  { id: 'indigo', color: '#6366f1', name: 'Indigo' },
  { id: 'purple', color: '#8b5cf6', name: 'Purple' },
  { id: 'pink', color: '#ec4899', name: 'Pink' },
  { id: 'rose', color: '#f43f5e', name: 'Rose' },
  { id: 'red', color: '#cf1616', name: 'Red' },
  { id: 'orange', color: '#f5700b', name: 'Orange' },
  { id: 'amber', color: '#fbbf24', name: 'Amber' },
  { id: 'yellow', color: '#eab308', name: 'Yellow' },
  { id: 'green', color: '#10b981', name: 'Green' },
  { id: 'emerald', color: '#34d399', name: 'Emerald' },
  { id: 'teal', color: '#14b8a6', name: 'Teal' },
  { id: 'cyan', color: '#06b6d4', name: 'Cyan' },
  { id: 'sky', color: '#0ea5e9', name: 'Sky' },
  { id: 'blue', color: '#3b82f6', name: 'Blue' }
];

// ============================================
// FEATURES
// ============================================
const FEATURES = [
  { icon: Pill, text: 'Pharmacy & Medical Store' },
  { icon: Sparkles, text: 'Cosmetics & Beauty Shop' },
  { icon: ShoppingCart, text: 'General Store (Kirana)' },
  { icon: ScissorsIcon, text: 'Tailor & Fabric Shop' },
  { icon: Shirt, text: 'Clothing (Readymade)' },
  { icon: Droplet, text: 'Paint & Hardware Store' },
  { icon: Wrench, text: 'Auto Workshop' },
  { icon: Car, text: 'Spare Parts Shop' },
  { icon: Bike, text: 'Motorcycle Workshop' },
  { icon: Home, text: 'Home Decor & Furniture' },
  { icon: Package, text: 'Electronics Shop' },
  { icon: Coffee, text: 'Restaurant & Cafe' }
];

// ============================================
// 3D SUBSCRIPTION POPUP
// ============================================
const SubscriptionPopup3D = ({ 
  isOpen, 
  onClose, 
  blockReason, 
  message, 
  subscriptionSummary, 
  subscriptions,
  userStatus,
  subscriptionStatus,
  translations,
  onRenew
}) => {
  const cardRef = useRef(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });

  if (!isOpen) return null;

  const getBlockConfig = () => {
    const configs = {
      'subscription_expired': {
        icon: 'fa-exclamation-triangle',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: translations.subscription_expired || '⚠️ Subscription Expired',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      },
      'subscription_cancelled': {
        icon: 'fa-times-circle',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: '#f59e0b',
        title: translations.subscription_cancelled || 'Subscription Cancelled',
        glowColor: 'rgba(245, 158, 11, 0.2)'
      },
      'subscription_pending': {
        icon: 'fa-hourglass-half',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: '#f59e0b',
        title: translations.subscription_pending || 'Subscription Pending',
        glowColor: 'rgba(245, 158, 11, 0.2)'
      },
      'no_subscription': {
        icon: 'fa-file-invoice',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: '#f59e0b',
        title: translations.no_subscription || 'No Subscription Found',
        glowColor: 'rgba(245, 158, 11, 0.2)'
      },
      'user_inactive': {
        icon: 'fa-user-slash',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: translations.user_inactive || 'Account Inactive',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      },
      'user_suspended': {
        icon: 'fa-ban',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: translations.user_suspended || 'Account Suspended',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      },
      'default': {
        icon: 'fa-exclamation-triangle',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: 'Access Denied',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      }
    };
    return configs[blockReason] || configs['default'];
  };

  const config = getBlockConfig();

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
  };

  return createPortal(
    <div className="oz-popup-3d-overlay" onClick={onClose}>
      <div className="oz-popup-3d-container" onClick={(e) => e.stopPropagation()}>
        <div 
          ref={cardRef}
          className="oz-popup-3d-card"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            transform: `perspective(800px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
          }}
        >
          <div className="oz-popup-3d-glow" style={{ background: config.glowColor }}></div>
          
          <div className="oz-popup-3d-icon-wrapper">
            <div className="oz-popup-3d-icon" style={{ background: config.bg, borderColor: config.border }}>
              <i className={`fas ${config.icon}`} style={{ color: config.color }}></i>
            </div>
          </div>

          <div className="oz-popup-3d-content">
            <h2 className="oz-popup-3d-title" style={{ color: config.color }}>
              {config.title}
            </h2>
            
            <p className="oz-popup-3d-message">{message}</p>

            {subscriptionSummary && (
              <div className="oz-popup-3d-summary">
                <div className="oz-popup-3d-summary-grid">
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-list"></i>
                      {translations.totalSubscriptions || 'Total'}
                    </span>
                    <span className="oz-popup-3d-summary-value">
                      {subscriptionSummary.total || 0}
                    </span>
                  </div>
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                      {translations.activeSubscriptions || 'Active'}
                    </span>
                    <span className="oz-popup-3d-summary-value oz-popup-3d-active">
                      {subscriptionSummary.active || 0}
                    </span>
                  </div>
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i>
                      {translations.expiredSubscriptions || 'Expired'}
                    </span>
                    <span className="oz-popup-3d-summary-value oz-popup-3d-expired">
                      {subscriptionSummary.expired || 0}
                    </span>
                  </div>
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-ban" style={{ color: '#f59e0b' }}></i>
                      {translations.cancelledSubscriptions || 'Cancelled'}
                    </span>
                    <span className="oz-popup-3d-summary-value oz-popup-3d-cancelled">
                      {subscriptionSummary.cancelled || 0}
                    </span>
                  </div>
                </div>
                
                {subscriptionSummary.days_until_expiry !== null && subscriptionSummary.days_until_expiry >= 0 && (
                  <div className="oz-popup-3d-days">
                    <i className="fas fa-clock"></i>
                    <span>
                      {translations.daysRemaining || 'Days Remaining'}: 
                      <strong>{subscriptionSummary.days_until_expiry}</strong> days
                    </span>
                  </div>
                )}
              </div>
            )}

            {(blockReason === 'subscription_expired' || blockReason === 'no_subscription') && (
              <button className="oz-popup-3d-renew-btn" onClick={onRenew}>
                <i className="fas fa-sync-alt"></i>
                <span>{blockReason === 'no_subscription' ? 'Get Subscription' : (translations.renewNow || 'Renew Now')}</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            )}

            <button className="oz-popup-3d-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
              <span>{translations.close || 'Close'}</span>
            </button>

            <div className="oz-popup-3d-footer">
              <p>
                <i className="fas fa-headset"></i>
                {translations.needHelp || 'Need Help?'} 
                <a href="https://wa.me/message/6D64QLRVE5YGG1" target="_blank" rel="noopener noreferrer">
                  {translations.contactSupport || 'Contact Support'}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ============================================
// MAIN LOGIN COMPONENT
// ============================================
function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [toast, setToast] = useState(null);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState('yellow');
  const [isDark, setIsDark] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isHoveringTheme, setIsHoveringTheme] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  
  // Language state
  const [language, setLanguage] = useState('en');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [texts, setTexts] = useState(TRANSLATIONS.en);
  
  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    blockReason: '',
    message: '',
    summary: null,
    subscriptions: null,
    userStatus: '',
    subscriptionStatus: ''
  });

  const containerRef = useRef(null);
  const themeRef = useRef(null);
  const themeButtonWrapperRef = useRef(null);
  const themePanelRef = useRef(null);
  const cardRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const currentColor = THEME_COLORS.find(t => t.id === selectedTheme)?.color || '#eab308';

  // ============================================
  // INITIALIZE FROM LOCAL STORAGE
  // ============================================
  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername');
    const savedRemember = localStorage.getItem('rememberMe');
    const savedTheme = localStorage.getItem('authTheme');
    const savedThemeMode = localStorage.getItem('authThemeMode');
    const savedLanguage = localStorage.getItem('authLanguage');

    if (savedRemember === 'true' && savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    if (savedTheme) {
      setSelectedTheme(savedTheme);
    }

    if (savedThemeMode === 'dark') {
      setIsDark(true);
    }

    if (savedLanguage && TRANSLATIONS[savedLanguage]) {
      setLanguage(savedLanguage);
      setTexts(TRANSLATIONS[savedLanguage]);
      applyLanguageDirection(savedLanguage);
    }

    applyThemeColors(currentColor);
  }, []);

  // ============================================
  // ROTATE FEATURES
  // ============================================
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % FEATURES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // ============================================
  // APPLY THEME
  // ============================================
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--auth-primary', currentColor);
    applyThemeColors(currentColor);

    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', currentColor);
  }, [currentColor]);

  // ============================================
  // APPLY THEME COLORS
  // ============================================
  const applyThemeColors = (color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = '#ef4444';
    }
    
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    const lighten = (amount) => {
      const newR = Math.min(255, r + amount);
      const newG = Math.min(255, g + amount);
      const newB = Math.min(255, b + amount);
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    };
    
    const darken = (amount) => {
      const newR = Math.max(0, r - amount);
      const newG = Math.max(0, g - amount);
      const newB = Math.max(0, b - amount);
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    };
    
    document.documentElement.style.setProperty('--oz-primary-color', color);
    document.documentElement.style.setProperty('--oz-primary-dark', darken(40));
    document.documentElement.style.setProperty('--oz-primary-light', lighten(40));
    document.documentElement.style.setProperty('--oz-primary-ultralight', lighten(80));
    document.documentElement.style.setProperty('--oz-primary-gradient', `linear-gradient(135deg, ${color}, ${darken(30)})`);
    document.documentElement.style.setProperty('--oz-primary-shadow', `${color}20`);
  };

  // ============================================
  // APPLY LANGUAGE DIRECTION
  // ============================================
  const applyLanguageDirection = (langCode) => {
    const langData = LANGUAGES[langCode];
    if (langData) {
      document.documentElement.dir = langData.dir;
      document.documentElement.lang = langCode;
      document.body.dir = langData.dir;
      document.documentElement.style.direction = langData.dir;
    }
  };

  // ============================================
  // HANDLE LANGUAGE CHANGE
  // ============================================
  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setTexts(TRANSLATIONS[langCode] || TRANSLATIONS.en);
    applyLanguageDirection(langCode);
    localStorage.setItem('authLanguage', langCode);
    setShowLanguageDropdown(false);
  };

  // ============================================
  // CLOSE DROPDOWNS (click outside)
  // ============================================
  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideWrapper = themeRef.current && themeRef.current.contains(e.target);
      const insidePanel = themePanelRef.current && themePanelRef.current.contains(e.target);
      const insideLangDropdown = languageDropdownRef.current && languageDropdownRef.current.contains(e.target);
      
      if (!insideWrapper && !insidePanel) {
        setIsThemeOpen(false);
      }
      
      if (!insideLangDropdown) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================
  // POSITION THE PORTAL PANEL
  // ============================================
  const updatePanelPosition = useCallback(() => {
    if (!cardRef.current || !themeButtonWrapperRef.current) return;
    const cardRect = cardRef.current.getBoundingClientRect();
    const btnRect = themeButtonWrapperRef.current.getBoundingClientRect();

    const gap = 2;
    const viewportPad = 6;
    const panelWidth = window.innerWidth <= 400 ? 48 : window.innerWidth <= 520 ? 56 : 64;

    let left = cardRect.left - panelWidth - gap;
    if (left < viewportPad) {
      left = Math.max(viewportPad, btnRect.left - panelWidth - gap);
    }

    setPanelStyle({
      left: `${left}px`,
      bottom: `${window.innerHeight - cardRect.bottom}px`,
      width: `${panelWidth}px`
    });
  }, []);

  useEffect(() => {
    if (!isThemeOpen) return;
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isThemeOpen, updatePanelPosition]);

  // ============================================
  // LOCK ZOOM
  // ============================================
  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousContent = viewportMeta ? viewportMeta.getAttribute('content') : null;
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.setAttribute('name', 'viewport');
      document.head.appendChild(viewportMeta);
    }
    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover'
    );

    const blockMultiTouch = (e) => {
      if (e.touches && e.touches.length > 1) e.preventDefault();
    };

    let lastTouchEnd = 0;
    const blockDoubleTapZoom = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 350) e.preventDefault();
      lastTouchEnd = now;
    };

    const blockGestureZoom = (e) => e.preventDefault();
    const blockCtrlWheelZoom = (e) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    };

    document.addEventListener('touchmove', blockMultiTouch, { passive: false });
    document.addEventListener('touchend', blockDoubleTapZoom, false);
    document.addEventListener('gesturestart', blockGestureZoom, { passive: false });
    document.addEventListener('wheel', blockCtrlWheelZoom, { passive: false });

    return () => {
      document.removeEventListener('touchmove', blockMultiTouch);
      document.removeEventListener('touchend', blockDoubleTapZoom);
      document.removeEventListener('gesturestart', blockGestureZoom);
      document.removeEventListener('wheel', blockCtrlWheelZoom);
      if (previousContent !== null) {
        viewportMeta.setAttribute('content', previousContent);
      }
    };
  }, []);

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ============================================
  // BUTTON HOVER EFFECT
  // ============================================
  useEffect(() => {
    const button = document.querySelector('.auth-login-button');
    if (!button) return;

    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      button.style.setProperty('--mouse-x', `${x}%`);
      button.style.setProperty('--mouse-y', `${y}%`);
    };

    const handleClick = (e) => {
      const rect = button.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      button.style.setProperty('--click-x', `${x}%`);
      button.style.setProperty('--click-y', `${y}%`);
    };

    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('click', handleClick);

    return () => {
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('click', handleClick);
    };
  }, []);

  // ============================================
  // GET DASHBOARD ROUTE
  // ============================================
  const getDashboardRoute = (role) => {
    if (role && ROUTES[role]) return ROUTES[role];
    return '/dashboard';
  };

  const handleRenew = () => {
    setShowPopup(false);
    window.open('https://wa.me/message/6D64QLRVE5YGG1', '_blank');
  };

  // ============================================
  // HANDLE LOGIN
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!username.trim()) {
      showToast('warning', 'Username Required', texts.username + ' is required');
      setIsLoading(false);
      return;
    }

    if (!password) {
      showToast('warning', 'Password Required', texts.password + ' is required');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/login.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username: username.trim(), 
          password: password,
          remember: rememberMe,
          theme_color: currentColor,
          language: language
        }),
      });
      
      const data = await response.json();
      
      if (!data.success && data.login_status) {
        setPopupData({
          blockReason: data.login_status,
          message: data.message,
          summary: data.subscription_summary || null,
          subscriptions: data.subscriptions || null,
          userStatus: data.user_status || '',
          subscriptionStatus: data.subscription_status || ''
        });
        setShowPopup(true);
        setIsLoading(false);
        return;
      }
      
      if (data.success) {
        const userRole = data.role;
        const isSuperAdmin = data.is_super_admin || userRole === 'super_admin' || userRole === 'superadmin';
        
        // Get the route based on role
        const route = getDashboardRoute(userRole);
        
        if (!route) {
          showToast('error', 'Access Denied', `No dashboard available for role: ${userRole}`);
          setIsLoading(false);
          return;
        }
        
        const userData = {
          id: data.user?.id,
          username: data.user?.username || username.trim(),
          fullname: data.user?.fullname || username.trim(),
          role: userRole,
          themeColor: currentColor,
          language: language,
          businessName: data.user?.business_name || "OurZest Corp",
          email: data.user?.email || `${username.trim()}@oz.com`,
          shop_id: data.user?.shop_id,
          shop_name: data.user?.shop_name,
          shop_code: data.user?.shop_code,
          status: data.user?.status || 'active',
          is_super_admin: isSuperAdmin || false,
          subscription: data.subscription || null,
          login_status: data.login_status || 'active'
        };
        
        // Save to localStorage
        localStorage.setItem('authTheme', selectedTheme);
        localStorage.setItem('authThemeMode', isDark ? 'dark' : 'light');
        localStorage.setItem('authLanguage', language);
        localStorage.setItem('user_role', userRole);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem("auth_timestamp", Date.now().toString());
        
        if (isSuperAdmin) {
          localStorage.setItem('is_super_admin', 'true');
        }
        
        if (data.subscription) {
          localStorage.setItem('user_subscription', JSON.stringify(data.subscription));
        }
        
        if (rememberMe) {
          localStorage.setItem('savedUsername', username);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('savedUsername');
          localStorage.setItem('rememberMe', 'false');
        }
        
        // Set user in context
        setUser(userData);
        
        // Show welcome toast
        const welcomeName = userData.fullname || userData.username;
        const welcomeMsg = isSuperAdmin ? 'Super Admin login successful!' : `Hello ${welcomeName}!`;
        showToast('success', isSuperAdmin ? 'Welcome Super Admin' : 'Welcome Back', welcomeMsg);
        
        // Navigate to the dashboard
        setTimeout(() => {
          navigate(route, { replace: true });
        }, 1500);
        
      } else {
        showToast('error', 'Access Denied', data.message || 'Invalid credentials');
        setPassword('');
        setIsLoading(false);
      }
      
    } catch (err) {
      console.error('Login error:', err);
      showToast('error', 'System Error', 'Cannot connect to server. Please check your connection.');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) handleSubmit(e);
  };

  const CurrentFeatureIcon = FEATURES[currentFeature]?.icon || Activity;

  const getToastIcon = (type) => {
    if (type === 'success') return <CheckCircle size={16} />;
    return <AlertCircle size={16} />;
  };

  const getToastStyle = (type) => {
    if (type === 'success') {
      return {
        iconBg: 'rgba(16, 185, 129, 0.15)',
        iconColor: '#10b981',
        borderColor: 'rgba(16, 185, 129, 0.2)'
      };
    }
    if (type === 'warning') {
      return {
        iconBg: 'rgba(245, 158, 11, 0.15)',
        iconColor: '#f59e0b',
        borderColor: 'rgba(245, 158, 11, 0.2)'
      };
    }
    return {
      iconBg: 'rgba(239, 68, 68, 0.15)',
      iconColor: '#ef4444',
      borderColor: 'rgba(239, 68, 68, 0.2)'
    };
  };

  const handleThemeToggle = () => {
    setIsThemeOpen(!isThemeOpen);
  };

  const handleColorSelect = (themeId) => {
    setSelectedTheme(themeId);
    const theme = THEME_COLORS.find(t => t.id === themeId);
    if (theme) {
      localStorage.setItem('authTheme', themeId);
    }
  };

  const handleModeToggle = (mode) => {
    setIsDark(mode === 'dark');
    localStorage.setItem('authThemeMode', mode === 'dark' ? 'dark' : 'light');
  };

  return (
    <div className={`auth-login-container ${isDark ? 'dark' : 'light'}`} ref={containerRef}>
      {/* 3D Subscription Popup */}
      <SubscriptionPopup3D 
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        blockReason={popupData.blockReason}
        message={popupData.message}
        subscriptionSummary={popupData.summary}
        subscriptions={popupData.subscriptions}
        userStatus={popupData.userStatus}
        subscriptionStatus={popupData.subscriptionStatus}
        translations={texts}
        onRenew={handleRenew}
      />
      
      {/* Gradient Background Overlay */}
      <div className="auth-gradient-background">
        <div className="auth-gradient-layer auth-gradient-layer-1" />
        <div className="auth-gradient-layer auth-gradient-layer-2" />
        <div className="auth-gradient-layer auth-gradient-layer-3" />
        <div className="auth-gradient-layer auth-gradient-layer-4" />
      </div>

      {/* Toast */}
      {toast && (
        <div className={`auth-login-toast ${toast.type}`} style={{ borderColor: getToastStyle(toast.type).borderColor }}>
          <div className="auth-login-toast-content">
            <div className="auth-login-toast-icon" style={{
              background: getToastStyle(toast.type).iconBg,
              color: getToastStyle(toast.type).iconColor
            }}>
              {getToastIcon(toast.type)}
            </div>
            <div className="auth-login-toast-text">
              <div className="auth-login-toast-title">{toast.title}</div>
              <div className="auth-login-toast-message">{toast.message}</div>
            </div>
            <button className="auth-login-toast-close" onClick={() => setToast(null)}>
              <X size={16} />
            </button>
          </div>
          <div className="auth-login-toast-timer">
            <div className="auth-login-toast-progress-bar" style={{
              background: toast.type === 'success' ? 'linear-gradient(90deg, #10b981, #059669)'
                : toast.type === 'warning' ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                : 'linear-gradient(90deg, #ef4444, #dc2626)'
            }} />
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="auth-login-card" ref={cardRef}>
        <div className="auth-login-brand">
          <div className="auth-brand-icon-wrapper">
            <div className="auth-brand-icon-ring">
              {logoError ? (
                <div className="auth-brand-icon" style={{ color: currentColor }}>
                  <Activity size={32} />
                </div>
              ) : (
                <img
                  src={logo}
                  alt="OURZEST"
                  className="auth-brand-logo"
                  onError={() => setLogoError(true)}
                />
              )}
            </div>
          </div>
          <h1 className="auth-brand-title">
            OURZEST-APP
          </h1>
          <p className="auth-brand-subtitle">{texts.access}</p>
        </div>

        <div className="auth-features-carousel">
          <div className="auth-feature-slide">
            <CurrentFeatureIcon size={18} className="auth-feature-icon" style={{ color: currentColor }} />
            <span className="auth-feature-text">{FEATURES[currentFeature].text}</span>
          </div>
          <div className="auth-feature-dots">
            {FEATURES.map((_, i) => (
              <span
                key={i}
                className={`auth-feature-dot ${i === currentFeature ? 'active' : ''}`}
                style={{ background: i === currentFeature ? currentColor : 'var(--auth-border)' }}
              />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-login-form" autoComplete="on">
          <div className="auth-login-form-group">
            <div className="auth-login-input-wrapper">
              <User className="auth-login-input-icon" size={18} />
              <input
                type="text"
                id="username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Username"
                autoComplete="username"
                autoFocus
                disabled={isLoading}
                className="auth-login-input"
                style={{ borderColor: `${currentColor}20` }}
              />
              <label htmlFor="username" style={{ color: 'var(--auth-label-color)' }}>{texts.username}</label>
            </div>
          </div>

          <div className="auth-login-form-group">
            <div className="auth-login-input-wrapper">
              <Lock className="auth-login-input-icon" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Password"
                autoComplete="current-password"
                disabled={isLoading}
                className="auth-login-input"
                style={{ borderColor: `${currentColor}20` }}
              />
              <label htmlFor="password" style={{ color: 'var(--auth-label-color)' }}>{texts.password}</label>
              <button
                type="button"
                className="auth-login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="auth-login-options">
            <label className="auth-login-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                style={{ accentColor: currentColor }}
              />
              <span>{texts.remember}</span>
            </label>
            <button
              type="button"
              className="auth-login-forgot-password"
              style={{ color: `${currentColor}80` }}
              onClick={() => showToast('warning', 'Reset Password', texts.forgot + '? Please contact your administrator')}
            >
              {texts.forgot}
            </button>
          </div>

          <button
            type="submit"
            className={`auth-login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
            style={{
              background: `linear-gradient(135deg, ${currentColor}, ${currentColor}dd)`,
              boxShadow: `0 10px 40px ${currentColor}30`
            }}
          >
            <span className="auth-login-button-text">
              {isLoading ? texts.loading : texts.signin}
            </span>
            {isLoading ? (
              <span className="auth-login-spinner" />
            ) : (
              <ArrowRight size={18} className="auth-login-button-icon" />
            )}
          </button>
        </form>

        <div className="auth-login-footer">
          <div className="auth-login-footer-links">
            <span>{texts.copyright}</span>
            <span className="auth-footer-divider">•</span>
            <button>Privacy</button>
            <span className="auth-footer-divider">•</span>
            <button>Terms</button>
            <span className="auth-footer-divider">•</span>
            <button>Support</button>
          </div>
          <div className="auth-login-version">
            <span>v3.0 Enterprise</span>
          </div>
        </div>

        {/* Theme Toggle */}
        <div className="auth-theme-wrapper" ref={themeRef}>
          <div
            className={`auth-theme-peek ${isThemeOpen ? 'expanded' : ''}`}
            onMouseEnter={() => setIsHoveringTheme(true)}
            onMouseLeave={() => setIsHoveringTheme(false)}
          >
            <button
              ref={themeButtonWrapperRef}
              type="button"
              className={`auth-theme-toggle-btn ${isThemeOpen ? 'open' : ''}`}
              onClick={handleThemeToggle}
              style={{
                background: isThemeOpen ? currentColor : 'var(--auth-card-bg)'
              }}
            >
              <Palette
                size={18}
                style={{ color: isThemeOpen ? '#ffffff' : currentColor }}
                className={isHoveringTheme && !isThemeOpen ? 'auth-theme-icon-hover' : ''}
              />
            </button>
          </div>
        </div>

        {/* Theme Panel Portal */}
        {isThemeOpen && createPortal(
          <div ref={themePanelRef} className="auth-theme-panel-portal" style={panelStyle}>
            <div className="auth-theme-panel-content">
              <div className="auth-theme-mode-group-vertical">
                <button
                  type="button"
                  className={`auth-theme-mode-btn-sm ${!isDark ? 'active' : ''}`}
                  onClick={() => handleModeToggle('light')}
                  style={{ borderColor: !isDark ? currentColor : 'transparent' }}
                  title="Light"
                >
                  <Sun size={12} />
                </button>
                <button
                  type="button"
                  className={`auth-theme-mode-btn-sm ${isDark ? 'active' : ''}`}
                  onClick={() => handleModeToggle('dark')}
                  style={{ borderColor: isDark ? currentColor : 'transparent' }}
                  title="Dark"
                >
                  <Moon size={12} />
                </button>
              </div>

              <div className="auth-theme-color-grid-vertical">
                {THEME_COLORS.map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    className={`auth-theme-color-btn-sm ${selectedTheme === theme.id ? 'active' : ''}`}
                    style={{
                      backgroundColor: theme.color,
                      borderColor: selectedTheme === theme.id ? '#ffffff' : 'transparent',
                      boxShadow: selectedTheme === theme.id ? `0 0 12px ${theme.color}80` : 'none'
                    }}
                    onClick={() => handleColorSelect(theme.id)}
                    title={theme.name}
                  >
                    {selectedTheme === theme.id && <span className="auth-theme-color-check-sm">✓</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

export default Login;